import React, { useState } from "react";
import { Check, ChevronDown, ChevronUp, Minus } from "lucide-react";
import { Link } from "react-router-dom";
import { PilotButton } from "../components/PilotIntake";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend
} from "recharts";

function Eyebrow({ children }) {
  return (
    <span
      className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] px-3 py-1.5 rounded-full"
      style={{
        color: "var(--primary-color)",
        backgroundColor: "var(--primary-50)",
        border: "1px solid color-mix(in srgb, var(--primary-color) 14%, transparent)",
      }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "var(--primary-color)" }} />
      {children}
    </span>
  );
}

const PALETTE = ["#8a1c1c", "#b86a6a", "#d6b8a8", "#c9c3b8", "#9c9aa1"];

const comparisonPoints = [
  { label: "Real-time polling", coursengage: true, CompetitorA: true, CompetitorB: false },
  { label: "Seamless student sign-in", coursengage: true, CompetitorA: false, CompetitorB: true },
  { label: "Instant analytics dashboard", coursengage: true, CompetitorA: false, CompetitorB: true },
  { label: "Mobile & web support", coursengage: true, CompetitorA: true, CompetitorB: false },
  { label: "Flexible question types", coursengage: true, CompetitorA: false, CompetitorB: true },
  { label: "Easy LMS integration", coursengage: true, CompetitorA: false, CompetitorB: true },
  { label: "AI session summaries", coursengage: true, CompetitorA: false, CompetitorB: false },
  { label: "Honest, transparent pricing", coursengage: true, CompetitorA: false, CompetitorB: false },
];

const featureDescriptions = {
  "Real-time polling": "Engage students with live polls during lectures with instant results visualization.",
  "Seamless student sign-in": "Students join with a class code, with no complicated setup required.",
  "Instant analytics dashboard": "Real-time insights into student responses and engagement metrics.",
  "Mobile & web support": "Participate from any device with a modern browser.",
  "Flexible question types": "Multiple choice, free response, image-based questions, and more.",
  "Easy LMS integration": "One-click integration with your campus LMS.",
  "AI session summaries": "An AI brief of what worked, what didn't, and what to revisit next.",
  "Honest, transparent pricing": "Limited-time instructor pilot, then a simple per-student rate.",
};

const experienceData = [
  { name: "Excellent", value: 40 },
  { name: "Good", value: 48 },
  { name: "Average", value: 8 },
  { name: "Poor", value: 4 },
];
const easeOfUseData = [
  { name: "Very Easy", value: 52 },
  { name: "Easy", value: 32 },
  { name: "Neutral", value: 16 },
];
const interfaceData = [
  { name: "Strongly agree", value: 52 },
  { name: "Agree", value: 32 },
  { name: "Neutral", value: 16 },
];

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        backgroundColor: "var(--card-bg)",
        border: "1px solid var(--border-color)",
        padding: "8px 12px",
        borderRadius: "8px",
        boxShadow: "var(--shadow-sm)",
        fontSize: "12px",
      }}>
        <span style={{ color: "var(--text-color)" }}>{`${payload[0].name}: ${payload[0].value}%`}</span>
      </div>
    );
  }
  return null;
};

const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.06) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#ffffff" textAnchor="middle" dominantBaseline="central"
      style={{ fontSize: "11px", fontWeight: 600 }}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const REASONS = [
  {
    n: "01",
    title: "Built for university teaching",
    body: "We're not a generic polling tool retrofitted for class. Every feature is shaped by how a 200-seat lecture hall actually behaves.",
  },
  {
    n: "02",
    title: "Modern instead of legacy",
    body: "No clicker hardware. No outdated portals. A clean web app that students join in seconds from any device they already own.",
  },
  {
    n: "03",
    title: "AI that earns its place",
    body: "Instant post-class summaries that actually save you time. No fluff, no novelty, just a quiet brief of what to teach next.",
  },
  {
    n: "04",
    title: "Honest pricing",
    body: "A limited-time pilot for instructors, then a single transparent per-student rate. No quotes, no minimums, no surprise renewals.",
  },
];

export default function WhyChooseUs() {
  const [expandedRows, setExpandedRows] = useState([]);

  const toggleRow = (index) =>
    setExpandedRows(expandedRows.includes(index)
      ? expandedRows.filter(i => i !== index)
      : [...expandedRows, index]);

  return (
    <main style={{ backgroundColor: "var(--bg-color)", color: "var(--text-color)" }}>
      {/* Hero */}
      <section className="pt-20 pb-16" style={{ background: "var(--gradient-soft)" }}>
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <div className="flex justify-center mb-5"><Eyebrow>Why CoursEngage</Eyebrow></div>
          <h1 className="text-4xl sm:text-6xl font-semibold tracking-tight" style={{ color: "var(--text-color)" }}>
            The quiet alternative to{" "}
            <span className="ce-serif italic" style={{ color: "var(--accent-color)" }}>noisy classroom tools.</span>
          </h1>
          <p className="mt-6 text-lg max-w-2xl mx-auto leading-relaxed" style={{ color: "var(--muted-color)" }}>
            Honest pricing, modern design, and AI that earns its keep, built for the way university classes actually work.
          </p>
        </div>
      </section>

      {/* Reasons */}
      <section className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px rounded-2xl overflow-hidden"
            style={{ backgroundColor: "var(--border-color)", border: "1px solid var(--border-color)" }}>
            {REASONS.map((r) => (
              <div key={r.n} className="p-8 sm:p-10" style={{ backgroundColor: "var(--card-bg)" }}>
                <div className="ce-serif text-2xl mb-3" style={{ color: "var(--primary-color)" }}>{r.n}</div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: "var(--text-color)" }}>{r.title}</h3>
                <p className="text-base leading-relaxed" style={{ color: "var(--muted-color)" }}>{r.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-20" style={{ backgroundColor: "var(--alt-bg)" }}>
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Eyebrow>Side by side</Eyebrow>
            <h2 className="mt-5 text-3xl sm:text-4xl font-semibold tracking-tight" style={{ color: "var(--text-color)" }}>
              How we compare.
            </h2>
          </div>

          <div
            className="rounded-2xl overflow-hidden"
            style={{ backgroundColor: "var(--card-bg)", border: "1px solid var(--border-color)" }}
          >
            <table className="w-full">
              <thead>
                <tr style={{ backgroundColor: "var(--alt-bg)", borderBottom: "1px solid var(--border-color)" }}>
                  <th className="p-4 text-left text-xs font-semibold uppercase tracking-[0.12em]" style={{ color: "var(--muted-color)" }}>Feature</th>
                  <th className="p-4 text-center text-xs font-semibold uppercase tracking-[0.12em]" style={{ color: "var(--primary-color)" }}>CoursEngage</th>
                  <th className="p-4 text-center text-xs font-semibold uppercase tracking-[0.12em]" style={{ color: "var(--muted-color)" }}>Competitor A</th>
                  <th className="p-4 text-center text-xs font-semibold uppercase tracking-[0.12em]" style={{ color: "var(--muted-color)" }}>Competitor B</th>
                </tr>
              </thead>
              <tbody>
                {comparisonPoints.map((pt, index) => (
                  <React.Fragment key={pt.label}>
                    <tr
                      className="cursor-pointer transition-colors"
                      style={{ borderTop: index === 0 ? "none" : "1px solid var(--border-color)" }}
                      onClick={() => toggleRow(index)}
                    >
                      <td className="p-4 font-medium" style={{ color: "var(--text-color)" }}>
                        <div className="flex items-center justify-between gap-3">
                          <span>{pt.label}</span>
                          {expandedRows.includes(index)
                            ? <ChevronUp className="w-4 h-4 opacity-60" />
                            : <ChevronDown className="w-4 h-4 opacity-60" />}
                        </div>
                      </td>
                      {[pt.coursengage, pt.CompetitorA, pt.CompetitorB].map((val, i) => (
                        <td key={i} className="p-4 text-center">
                          {val ? (
                            <Check className="inline-block w-5 h-5" style={{ color: i === 0 ? "var(--primary-color)" : "var(--text-soft)" }} />
                          ) : (
                            <Minus className="inline-block w-4 h-4" style={{ color: "var(--subtle-color)" }} />
                          )}
                        </td>
                      ))}
                    </tr>
                    {expandedRows.includes(index) && (
                      <tr style={{ backgroundColor: "var(--alt-bg)" }}>
                        <td colSpan="4" className="px-5 py-4 text-sm" style={{ color: "var(--muted-color)" }}>
                          {featureDescriptions[pt.label]}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Feedback */}
      <section className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Eyebrow>From the classroom</Eyebrow>
            <h2 className="mt-5 text-3xl sm:text-4xl font-semibold tracking-tight" style={{ color: "var(--text-color)" }}>
              Student feedback from BIOC 431/831.
            </h2>
            <p className="mt-4 text-sm" style={{ color: "var(--muted-color)" }}>
              Collected via Google Form, 25 responses, December 2025 · UNL Biochemistry
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <ChartCard title="Overall experience">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={experienceData} cx="50%" cy="50%" labelLine={false} label={renderLabel} outerRadius={90} dataKey="value">
                    {experienceData.map((_, i) => (
                      <Cell key={i} fill={PALETTE[i]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11, color: "var(--muted-color)" }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Ease of joining">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={easeOfUseData} layout="vertical" margin={{ top: 5, right: 16, left: 0, bottom: 5 }}>
                  <XAxis type="number" tick={{ fill: "var(--muted-color)", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis dataKey="name" type="category" tick={{ fill: "var(--text-color)", fontSize: 12 }} axisLine={false} tickLine={false} width={80} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: "var(--alt-bg)" }} />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                    {easeOfUseData.map((_, i) => <Cell key={i} fill={PALETTE[i]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Interface user-friendliness">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={interfaceData} cx="50%" cy="50%" labelLine={false} label={renderLabel} outerRadius={90} dataKey="value">
                    {interfaceData.map((_, i) => <Cell key={i} fill={PALETTE[i]} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11, color: "var(--muted-color)" }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="rounded-3xl p-12 sm:p-16 text-center"
            style={{ backgroundColor: "var(--alt-bg)", border: "1px solid var(--border-color)" }}>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ color: "var(--text-color)" }}>
              Ready to try it for{" "}
              <span className="ce-serif italic" style={{ color: "var(--accent-color)" }}>yourself?</span>
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <PilotButton className="ce-btn ce-btn-primary text-base px-6 py-3.5">Join the pilot</PilotButton>
              <Link to="/product-detail" className="ce-btn ce-btn-ghost text-base px-6 py-3.5">See the product →</Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="rounded-2xl p-6"
      style={{ backgroundColor: "var(--card-bg)", border: "1px solid var(--border-color)", boxShadow: "var(--shadow-sm)" }}>
      <h3 className="text-sm font-semibold mb-4" style={{ color: "var(--text-color)" }}>{title}</h3>
      {children}
    </div>
  );
}
